/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import { useSelector } from 'react-redux';
import { Icon as CoreIcon } from '@material-ui/core';
import CardBody from './CardBody/CardBody';
import CardActions from './CardActions/CardActions';
import CardContextProvider from './CardContext';
import { selectVersion } from '../../../redux/features/configuration/configuration.slice';
import { dataTestId } from '../../../shared/utils/versionUtils';
import { KeyboardActions } from '../../../shared/enums/KeyboardActions';
import Tooltip from '../Tooltip';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    minHeight: '14rem',
    minWidth: '18rem',
    boxShadow: '0px 1px 3px 0px rgba(63,63,68,.15)',
    [theme.breakpoints.down('sm')]: {
      minWidth: '9rem',
    },
  },
  cardSelectable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.cardActive,
    },
  },
  cardSelected: {
    backgroundColor: theme.palette.cardActive,
    border: `0.125rem solid ${theme.palette.cardActiveBorder}`,
  },
  title: {
    width: '17.5rem',
  },
  content: {
    textAlign: 'left',
    marginRight: theme.spacing(2),
    alignItems: 'center',
  },
  multiEntry: {
    textAlign: 'left',
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  noData: {
    textAlign: 'center',
  },
  divider: {
    marginBottom: theme.spacing(1),
  },
  entryKey: {
    fontSize: '1rem',
    color: '#21252C',
    fontWeight: 'normal',
  },
  entryValue: {
    fontWeight: 'bold',
    color: theme.palette.tertiary.active,
  },
  attachmentEntryKey: {
    fontWeight: 'bold',
  },
  attachmentEntryValue: {
    fontSize: '0.875rem',
  },
  feesEntryKey: {
    fontSize: '1rem',
  },
  feesEntryValue: {
    fontWeight: 'bold',
    fontSize: '1rem',
  },
  partyEntryKey: {
    fontSize: '1rem',
  },
  partyEntryValue: {
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  seeMoreBox: {
    borderRadius: '25%',
    backgroundColor: theme.palette.seeMoreBackground,
    padding: '0.188rem 0.35rem 0.188rem 0.35rem',
  },
  seeAll: {
    fontSize: '1rem',
    textDecoration: 'underline',
    color: theme.palette.linkColour,
    '&:hover': {
      cursor: 'pointer',
    },
  },
  declarationExtra: {
    borderRadius: '25%',
    backgroundColor: theme.palette.seeMoreBackground,
    padding: '0.188rem 0.35rem 0.188rem 0.35rem',
  },
  declarationSeeAll: {
    fontSize: '1rem',
    textDecoration: 'underline',
    color: theme.palette.linkColour,
    '&:hover': {
      cursor: 'pointer',
    },
  },
  typography: {
    body2: {
      fontWeight: 'bold',
      align: 'right',
    },
  },
  entryValueGridItem: {
    maxWidth: '14rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',

    '&.additionalNotes': {
      maxWidth: 'unset',
      whiteSpace: 'unset',
      maxHeight: '4.5rem',
      cursor: 'default',
      overflow: 'hidden',
      display: '-webkit-box',
      '-webkit-line-clamp': '3',
      '-webkit-box-orient': 'vertical',
    },
  },
  entryIcon: {
    minWidth: '2rem',
  },
  scrollLink: {
    cursor: 'pointer',
    color: `${theme.palette.linkColour}`,
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'underline',
      color: `${theme.palette.buttonTextHover} !important`,
    },
    '&:focus': {
      textDecoration: 'underline',
      outline: `calc(2rem / 16) solid ${theme.palette.buttonTextHover}`,
    },
  },
  noDataLabel: {
    fontSize: '1rem',
  },
}));

const useStylesCardHeader = makeStyles({
  content: {
    overflow: 'auto',
  },
});

export default function SummaryCard({
  configurableIcon,
  Icon,
  cardTitle,
  subheader,
  entries,
  sectionName,
  actionType,
  handleCardClick,
  handleClick,
  handleEdit,
  handleDelete,
  handleClose,
  anchorEl,
  isSelected,
  isSummary,
  readOnly = false,
  isSelectable = false,
  id,
  className,
  customClassName,
  submission,
  onSectionChange,
  enableTooltip,
  actions,
}) {
  const classes = useStyles();
  const classesCardHeader = useStylesCardHeader();
  const selectedVersion = useSelector(selectVersion);

  const handleKeyDown = (e) => {
    if (e.keyCode === KeyboardActions.ENTER) {
      handleCardClick();
    }
  };

  let avatar = Icon && <Icon />;

  if (configurableIcon) {
    avatar = <CoreIcon>{configurableIcon}</CoreIcon>;
  }

  return (
    <CardContextProvider
      classes={classes}
      entries={entries}
      isSummary={isSummary}
      sectionName={sectionName}
      readOnly={readOnly}
      actionType={actionType}
      handleEdit={handleEdit}
      handleDelete={handleDelete}
      handleClick={handleClick}
      anchorEl={anchorEl}
      handleClose={handleClose}
      submission={submission}
      onSectionChange={onSectionChange}
      actions={actions}
    >
      <Card
        key={cardTitle}
        className={clsx(
          classes.root,
          className,
          isSelected && classes.cardSelected,
          handleCardClick && isSelectable && classes.cardSelectable,
        )}
        onClick={handleCardClick}
        onKeyDown={isSelectable ? handleKeyDown : undefined}
        id={id}
        tabIndex="0"
        {...dataTestId({ value: `${sectionName}-summary--${selectedVersion.value}` })}
      >
        <Tooltip
          title={cardTitle}
          placement="top-start"
          arrow
          disableHoverListener={!enableTooltip || cardTitle?.length < 20}
          hideAria={true}
        >
          <div>
            <CardHeader
              className={classes.cardHeader}
              titleTypographyProps={{ variant: 'h4', component: 'span', noWrap: true }}
              avatar={avatar}
              title={cardTitle}
              action={<CardActions id={id} />}
              subheader={subheader}
              classes={classesCardHeader}
            />
          </div>
        </Tooltip>
        <Divider />
        <CardBody className={classes.cardContent} customClassName={customClassName} />
      </Card>
    </CardContextProvider>
  );
}
